import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { registerGuest } from "../api/guest";
import COUNTRIES from "../data/countries";
import "./AuthForm.css";

// "Save and register": upgrades the current guest account into a real one,
// keeping the team the visitor just built. The team name is chosen here (it's
// only globally reserved on registration). On success the app switches out of
// guest mode (user.is_guest becomes false) and lands on the dashboard.
export default function GuestRegisterModal({ onClose }) {
  const { t }        = useTranslation();
  const { login }    = useAuth();
  const navigate     = useNavigate();
  const [form, setForm] = useState({ teamName: "", username: "", email: "", password: "", country: "" });
  const [error, setError] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const teamNameRef = useRef(null);

  useEffect(() => { teamNameRef.current?.focus(); }, []);
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const update = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const { access_token } = await registerGuest({
        Name: form.username.trim(),
        Password: form.password.trim(),
        Email: form.email.trim() || null,
        Country: form.country || null,
        TeamName: form.teamName.trim(),
      });
      await login(access_token);   // re-fetches /me → is_guest now false
      onClose?.();
      navigate("/dashboard");
    } catch (err) {
      const msg = err?.detail || err?.message;
      setError(msg === "TEAM_NAME_TAKEN" ? t("team.nameTaken") : (msg || t("guest.registerError")));
      setSaving(false);
    }
  };

  return (
    <form className="auth-form" onSubmit={handleSubmit}>
      <h2 className="auth-title">{t("guest.registerTitle")}</h2>
      <p className="auth-warning">{t("guest.registerIntro")}</p>
      {error && <p className="auth-error">{error}</p>}

      <input
        ref={teamNameRef}
        type="text"
        placeholder={t("guest.teamNamePlaceholder")}
        name="new-teamname"
        autoComplete="off"
        data-lpignore="true"
        data-1p-ignore="true"
        data-bwignore="true"
        data-protonpass-ignore="true"
        data-form-type="other"
        value={form.teamName}
        onChange={update("teamName")}
        required
        maxLength={50}
      />
      <input
        type="text"
        placeholder={t("guest.usernamePlaceholder")}
        name="new-username"
        autoComplete="off"
        // Opt this field out of password managers / email-mask extensions so
        // they don't make a plain username look like a credential/email field.
        data-lpignore="true"
        data-1p-ignore="true"
        data-bwignore="true"
        data-protonpass-ignore="true"
        data-form-type="other"
        value={form.username}
        onChange={update("username")}
        required
        minLength={3}
        maxLength={20}
      />
      <input
        type="email"
        placeholder={t("guest.emailPlaceholder")}
        autoComplete="email"
        value={form.email}
        onChange={update("email")}
      />
      {!form.email && <p className="auth-warning">{t("guest.noEmailWarning")}</p>}
      <div className="auth-pw-wrap">
        <input
          type={showPw ? "text" : "password"}
          placeholder={t("guest.passwordPlaceholder")}
          autoComplete="new-password"
          value={form.password}
          onChange={update("password")}
          required
          minLength={8}
        />
        <button type="button" className="auth-pw-toggle" onClick={() => setShowPw((v) => !v)} tabIndex={-1}>
          {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
        </button>
      </div>
      <select className="auth-select" value={form.country} onChange={update("country")}>
        <option value="">{t("guest.countryPlaceholder")}</option>
        <option value="XX">{t("guest.countryNeutral")}</option>
        {COUNTRIES.map((c) => (
          <option key={c.code} value={c.code}>{c.name}</option>
        ))}
      </select>

      <div className="auth-actions">
        <button type="submit" disabled={saving}>
          {saving ? t("guest.saving") : t("guest.registerSubmit")}
        </button>
        <button type="button" className="auth-cancel" onClick={onClose}>{t("guest.cancel")}</button>
      </div>
    </form>
  );
}
